import { useHandler } from "@redotech/react-util/hook";
import {
  rcoCustomerTagsSegmentSchema,
  RcoCustomerTagsSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  CustomerTagsSegmentListMultipleItemMembershipOperator,
  CustomerTagsSegmentListMultipleItemMembershipOperator as CustomerTagsSegmentListMultipleItemMembershipOperatorEnum,
  SegmentListMultipleItemMembershipOperator,
} from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { OperatorSelector } from "@redotech/redo-web/conversion/operator-selector";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";
import { CustomerTagsSelector } from "./customer-tags-selector";

export const CustomerTagsInspectorContent = memo(
  function CustomerTagsInspectorContent({ step }: { step: RcoSegmentStep }) {
    const segment = useMemo(
      () => rcoCustomerTagsSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {operatorsToText(segment.operator)}
          </Text>
        </Flex>
        <Flex dir="row" gap="md" wrap="wrap">
          {segment.tags.map((tag, index) => (
            <RedoBadge color="gray" key={index} size="sm" text={tag} />
          ))}
        </Flex>
      </>
    );
  },
);

const operatorsToText = (
  operator: RcoCustomerTagsSegmentSchema["operator"],
) => {
  switch (operator) {
    case SegmentListMultipleItemMembershipOperator.ANY_IN:
      return "Customer has at least one tag in the list:";
    case SegmentListMultipleItemMembershipOperator.NONE_IN:
      return "Customer does not have any tag in the list:";
    default:
      assertNever(operator);
  }
};

export const customerTagsSegmentListMultipleItemMembershipOperatorToText: Record<
  CustomerTagsSegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "at least one",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "no",
};

export const EditableCustomerTagsInspectorContent = memo(
  function EditableCustomerTagsInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const segment = useMemo(
      () => rcoCustomerTagsSegmentSchema.parse(step.segment),
      [step],
    );

    const handleOperatorSelected = useHandler(
      (operator: CustomerTagsSegmentListMultipleItemMembershipOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleTagsSelected = useHandler((tags: string[]) => {
      const updatedSegment = { ...segment, tags };
      setStep({ ...step, segment: updatedSegment });
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            Customer has
          </Text>
        </Flex>
        <Flex>
          <OperatorSelector<CustomerTagsSegmentListMultipleItemMembershipOperator>
            availableOptions={
              CustomerTagsSegmentListMultipleItemMembershipOperatorEnum
            }
            onOperatorSelected={handleOperatorSelected}
            operatorToText={
              customerTagsSegmentListMultipleItemMembershipOperatorToText
            }
            selectedOperator={segment.operator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          tag in the list:
        </Text>
        <CustomerTagsSelector
          onTagsSelected={handleTagsSelected}
          selectedTags={segment.tags}
        />
      </>
    );
  },
);
